import React, { ReactNode } from 'react';

import { useFieldState } from 'modules/shared-menu/hooks/useFieldState';

import { BaseField } from './Fields/types';
import { FormRows } from './FormRows';
import { WithCatalogLinkFormRow } from './WithCatalogLinkFormRow';
import { WithCreateTagFormRow, WithCreateTagFormRowProps } from './WithCreateTagFormRow';
import { ProductFormValues } from './types';
import { useProductOverridesContext } from '../../context/ProductOverridesContext';
import { Offer } from '../../types';

type FormRowRendererProps = WithCreateTagFormRowProps & {
  offer: Offer | undefined;
  previewComponent: ReactNode;
  editComponent: ReactNode;
  sidePanelMode: BaseField['sidePanelMode'];
  hasContentEditPermission: boolean;
  isOverridden?: boolean;
  canBeOverridden?: boolean;
  isOnlyForVenueLevel?: boolean;
  catalogName?: keyof ProductFormValues;
};

/**
 * This component receives, amongst other props, the `editComponent` and `previewComponent` for any given field and wraps the correct FormRow component around it depending on several field states variations (preview, locked, overridden, edit)
 */
export const FormRowRenderer = ({
  label,
  sidePanelMode,
  editComponent,
  previewComponent,
  offer,
  required,
  shouldDisplayCreateTag = false,
  isOverridden = false,
  canBeOverridden = false,
  shouldDisplayEditByDefault = false,
  hasContentEditPermission,
  isOnlyForVenueLevel = false,
  catalogName,
  ...rest
}: FormRowRendererProps) => {
  const fieldState = useFieldState({
    hasContentEditPermission,
    sidePanelMode,
    isOverridden,
    canBeOverridden,
    isOnlyForVenueLevel,
  });

  const shouldDisplayLocked = fieldState === 'previewing-locked';
  const shouldDisplayOverriddenPreview = fieldState === 'previewing-overridden';
  const shouldDisplayPreview = fieldState === 'previewing';
  const shouldDisplayWithCreateTag = fieldState === 'editing';
  const { connectedProductId } = useProductOverridesContext();

  if (shouldDisplayPreview) {
    return (
      <FormRows.Preview label={label} {...rest}>
        {previewComponent}
      </FormRows.Preview>
    );
  }

  if (shouldDisplayLocked) {
    return previewComponent ? (
      <FormRows.Locked label={label} {...rest}>
        {previewComponent}
      </FormRows.Locked>
    ) : null;
  }

  if (shouldDisplayOverriddenPreview) {
    return (
      <FormRows.Overridden label={label} {...rest}>
        {previewComponent}
      </FormRows.Overridden>
    );
  }

  if (catalogName && !!connectedProductId) {
    return (
      <WithCatalogLinkFormRow label={label} attrName={catalogName} required={required}>
        {editComponent}
      </WithCatalogLinkFormRow>
    );
  }

  if (shouldDisplayWithCreateTag) {
    return (
      <WithCreateTagFormRow
        label={label}
        shouldDisplayCreateTag={shouldDisplayCreateTag}
        shouldDisplayEditByDefault={shouldDisplayEditByDefault}
        required={required}
      >
        {editComponent}
      </WithCreateTagFormRow>
    );
  }

  return null;
};
